import {
	HttpClient,
	HttpErrorResponse,
	HttpHeaders,
	HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, Observer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Curp } from 'src/app/models/apis/curp.entities';

import {
	CmvHttpEntidadResponse,
	CmvHttpResponse,
	CobisCmvTokenHttpResponse,
	CobisCmvHttpResponse,
	InternListCmvHttpResponse,
	ExternListCmvHttpResponse,
} from 'src/app/models/apis/entities';
import {
	DigitalRequest,
	DigitalRequestResponse,
	DeclinedOffer,
	InterviewIdRequest,
	PreApplicationEvaluationResult,
	PreApplicationId,
	PreOffer,
	SimulatePaymentRequest,
	SimulatePaymentResponse,
	SaveCreditDataRequest,
	PreOfferResponse,
	LocationToSendCardRequest,
	ContactDataResponse,
	CreatePreOfferResponse,
} from 'src/app/models/apis/pre-offer';
import { environment } from 'src/environments/environment';
import { HttpHeadersBuilder } from './utils/http-header-builder';
import { AditionalDataRequest } from 'src/app/models/apis/aditional-data';
import { IneInformation } from 'src/app/models/apis/ine-information';
import { PreApplicationActions } from 'src/app/state/actions/pre-application.actions';
import {
	ApplicantType,
	PreApplicationState,
} from 'src/app/models/state/pre-application.model';
import { capitalizeFirstLetterOfWords } from 'src/app/data/utils/strings.utils';
import { ERROR_MESSAGES } from 'src/app/data/error-messages.data';
import {
	DataContactRequest,
	PersonalData,
} from 'src/app/models/personal-data.model';
import {
	selectCreditParameters,
	selectPreAplicationState,
} from 'src/app/state/selectors/pre-application.selectors';
import {
	EvaluationDataRequest,
	PreApplicationEvaluation,
} from 'src/app/models/pre-application/applicant.model';
import { Coords } from 'src/app/data/utils/geolocation.utils';
import { PersonalJobRequest } from 'src/app/models/apis/personal-job.model';
import {
	DigitalProcessRequest,
	DigitalProcessResponse,
} from 'src/app/models/apis/digital-process.model';
import {
	ContactData,
	FullProcessResponse,
	ProductResponse,
} from 'src/app/models/apis/onboarding-process.model';
import { InitialInformationModel } from 'src/app/models/apis/initial-information.model';
import { CertificatesResponse } from 'src/app/models/apis/certificates.model';
import { GeolocationService } from '../utils/geolocation.service';
import { createDigitalRequestBody } from 'src/app/models/pre-application/digitalRequest.builder';
import {
	ProofOfAddressRequest,
	ProofOfAddressResponse,
} from 'src/app/models/apis/proof-of-address.model';
import { MotorOfferErrorResponse, OfferResponse } from 'src/app/models/apis/offer.model';
import { DownloadModelo } from 'src/app/services/utils/download-from-base64.service';
import { resolveErrorMessage } from 'src/app/data/utils/http-error.util';
export interface DigitalRequestCreationResult {
	status: 'created' | 'alreadyExists';
	folioDigital?: string;
	errorDetail?: string;
}

type PreApplicationValidationModel = {
	is_presolicitud_rechazada: boolean;
	motivo_rechazo_identidad_validator?: string;
	motivo_rechazo_oferta_existente?: string;
	motivo_rechazo?: string;
};
type PreApplicationValidationResult = {
	isRejected: boolean;
	reason?: string;
};

export type VoucherDigitalResponse = {
	modelo: DownloadModelo;
	codigo: number;
	mensaje: string;
	estado: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class OnboardingService {
	urlBase = `${environment.urlBase}/onboarding/`;
	headers_: HttpHeaders;
	constructor(
		private _http: HttpClient,
		private store: Store,
		private geolocationService: GeolocationService
	) {
		this.headers_ = new HttpHeaders().set(
			'X-IBM-Client-Id',
			environment.clientID
		);
	}

	register(data: any) {
		const headers = new HttpHeaders().set(
			'X-IBM-Client-Id',
			environment.clientID
		);
		return this._http.post(`${this.urlBase}registro_onboarding`, data, {
			headers: headers,
		});
	}

	simulatePayment(
		data: SimulatePaymentRequest
	): Observable<SimulatePaymentResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<Array<SimulatePaymentResponse>>>(
				`${this.urlBase}simulate_payment`,
				data,
				{
					headers,
				}
			)
			.pipe(
				map(
					(response: CobisCmvHttpResponse<Array<SimulatePaymentResponse>>) => {
						if (response.estado && response.codigo === 200) {
							return response.modelo[0];
						}
						throw new Error(response.mensaje);
					}
				)
			);
	}

	saveCreditData(data: SaveCreditDataRequest): Observable<boolean> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<boolean>>(
				`${this.urlBase}presolicitud_datos_credito`,
				data,
				{
					headers,
				}
			)
			.pipe(
				map((response: CobisCmvHttpResponse<boolean>) => {
					const { estado, codigo, mensaje, modelo } = response;
					if (
						estado &&
						(codigo === 200 || codigo === '200' || codigo === 0 || codigo === '0')
					) {
						return !!modelo;
					}
					throw new Error(mensaje);
				})
			);
	}

	createPreOfferBody(preApplicationId: number): Observable<PreOffer> {
		return new Observable(observer => {
			this.store
				.select(selectCreditParameters)
				.pipe(take(1))
				.subscribe({
					next: creditParameters => {
						const preOfferBody: PreOffer = {
							curp: '',
							rfc: 'personalData.rfc',
							contrasena_usuario: null,
							folio_digital: '3637896953',
							id_producto: creditParameters.productId,
							codigo_producto_core: creditParameters.productCobisId,
							id_persona: 976699,
							id_presolicitud: preApplicationId,
						};
						observer.next(preOfferBody);
					},
				});
		});
	}

	createContactData(
		contact: DataContactRequest
	): Observable<ContactDataResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<ContactDataResponse>>(
				`${this.urlBase}guardar_datos_contacto`,
				contact,
				{ headers: headers }
			)
			.pipe(
				map((response: CobisCmvHttpResponse<ContactDataResponse>) => {
					const { estado, codigo } = response;
					if (estado && codigo === 200) {
						return response.modelo;
					}
					if (codigo === '407') {
						let error: Error = new Error(response.mensaje);
						error.name = 'Validation';
						throw error;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	requestPreOffer(preOffer: any): Observable<CreatePreOfferResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<CreatePreOfferResponse>>(
				`${this.urlBase}presolicitudes`,
				preOffer,
				{ headers: headers }
			)
			.pipe(
				map((response: CobisCmvHttpResponse<CreatePreOfferResponse>) => {
					const { estado, codigo } = response;
					if (estado && codigo === 200) {
						return response.modelo;
					}
					if (codigo === '407') {
						let error: Error = new Error(response.mensaje);
						error.name = 'Validation';
						throw error;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	savePreOfferDataToStore(
		responseEntity: PreOfferResponse,
		preOffer: PreOffer
	) {
		this.setApplicantDataToStore(preOffer);
		const idLead = responseEntity.id_persona;
		if (idLead && idLead > 0) {
			this.store.dispatch(
				PreApplicationActions.addApplicantIdForNewLeads({
					applicantId: idLead,
				})
			);
		}
	}

	requestSaveAddress(address: any): Observable<CreatePreOfferResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<CreatePreOfferResponse>>(
				`${this.urlBase}insertarDomicilio`,
				address,
				{ headers: headers }
			)
			.pipe(
				map((response: CobisCmvHttpResponse<CreatePreOfferResponse>) => {
					const { estado, codigo } = response;
					if (estado && codigo === 200) {
						return response.modelo;
					}
					if (codigo === '400') {
						let error: Error = new Error(response.mensaje);
						error.name = 'Validation';
						throw error;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	private setApplicantDataToStore(preOffer: PreOffer) {
		const isLead: boolean = preOffer.id_persona == 0;
		const isMember: boolean = preOffer.id_persona !== 0;
		let applicantId: number = 0;
		let applicantType: ApplicantType = ApplicantType.UNREGISTERED_LEAD;
		if (isLead) {
			applicantId = preOffer.id_persona;
			applicantType = ApplicantType.LEAD;
		}
		if (isMember) {
			applicantId = preOffer.id_persona;
			applicantType = ApplicantType.MEMBER;
		}

		// this.store.dispatch(
		// 	PreApplicationActions.addApplicantData({
		// 		applicantName: {
		// 			name: capitalizeFirstLetterOfWords(preOffer.nombre_s),
		// 			firstSurname: capitalizeFirstLetterOfWords(preOffer.apellido_paterno),
		// 			secondSurname: capitalizeFirstLetterOfWords(
		// 				preOffer.apellido_materno
		// 			),
		// 		},
		// 		applicantId,
		// 		applicantType,
		// 		applicantPhone: preOffer.celular,
		// 		applicantBirthDate: preOffer.fecha_nacimiento,
		// 		isApplicantAMember: isMember,
		// 		email: preOffer.correo,
		// 		curp: preOffer.curp,
		// 	})
		// );
	}

	evaluatePreApplication(
		preApplicationId: PreApplicationId
	): Observable<PreApplicationEvaluation> {
		const headers: HttpHeaders = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		return this._http
			.post<
				CmvHttpEntidadResponse<PreApplicationEvaluationResult | DeclinedOffer>
			>(`${this.urlBase}evaluatePreRequest`, preApplicationId, { headers })
			.pipe(
				map(
					(
						response: CmvHttpEntidadResponse<
							PreApplicationEvaluationResult | DeclinedOffer
						>
					) => {
						const { Estado, Codigo, Entidad } = response;
						if (Estado && Codigo === '200') {
							const entity = Entidad as PreApplicationEvaluationResult;
							const preApplicationEvaluation: PreApplicationEvaluation = {
								approved: entity.aprobado,
								allowRetry: entity.permite_reintento,
							};
							return preApplicationEvaluation;
						}
						if (Codigo === '407' || Codigo === '404') {
							const entity = Entidad as DeclinedOffer;
							const preApplicationEvaluation: PreApplicationEvaluation = {
								approved: false,
								allowRetry: entity.permite_reintento,
								declinedCode: entity.codigo_rechazo,
								message: response.Mensaje,
							};
							return preApplicationEvaluation;
						}
						throw new Error(response.Mensaje);
					}
				)
			);
	}

	validateInformationFromIne(curp: Curp): Observable<boolean> {
		const headers: HttpHeaders = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		return this._http
			.post<CmvHttpEntidadResponse<boolean>>(
				`${this.urlBase}validarInfoIne`,
				curp,
				{ headers: headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<boolean>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Estado;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	saveIneInfo(ineInfo: IneInformation): Observable<boolean> {
		const headers: HttpHeaders = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<any>>(`${this.urlBase}saveInfoIne`, ineInfo, {
				headers: headers,
			})
			.pipe(
				map((response: CobisCmvHttpResponse<any>) => {
					const { estado, codigo } = response;
					if (estado && codigo === 200) {
						return response.modelo;
					}
					if (codigo === '400') {
						let error: Error = new Error(response.mensaje);
						error.name = 'Validation';
						throw error;
					}
					throw new Error(response.mensaje);
				}),
				catchError((error: HttpErrorResponse | Error) => {
					if (error instanceof HttpErrorResponse) {
						if (error.status === 400 || error.status === 404) {
							return throwError(
								() =>
									new Error(
										resolveErrorMessage(error, 'Ocurrio un error inesperado.')
									)
							);
						}
					}
					return throwError(() => error);
				})
			);
	}

	saveAditionalInfo(aditionalInfo: AditionalDataRequest): Observable<string> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		return this._http
			.post<CmvHttpEntidadResponse<string>>(
				`${this.urlBase}aditionalInfo`,
				aditionalInfo,
				{ headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<string>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	saveInterviewId(interviewIdBody: InterviewIdRequest): Observable<boolean> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this._http
			.post<CmvHttpEntidadResponse<string>>(
				`${this.urlBase}saveInverviewID`,
				interviewIdBody,
				{ headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<string>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Estado;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getDigitalVoucher(
		preApplicationId: number,
		folio: string
	): Observable<string> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('id_presolicitud', preApplicationId)
			.set('folioDigital', folio);
		return this._http
			.post<CmvHttpEntidadResponse<string>>(
				`${this.urlBase}voucherDigital`,
				null,
				{
					headers,
					params: params,
				}
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<string>) => {
					if (response.Estado && response.Codigo === '201') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	postDigitalVoucherBySolicitudId(
		id_solicitud: number
	): Observable<VoucherDigitalResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelIdGd()
			.getHeaders()
			.set('Content-Type', 'application/json');

		return this._http.post<VoucherDigitalResponse>(
			`${this.urlBase}voucherDigital`,
			{ id_solicitud },
			{ headers }
		);
	}

	insertDigitalRequest(
		digitalRequest: DigitalRequest
	): Observable<DigitalRequestResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();

		return this._http
			.post<CobisCmvHttpResponse<DigitalRequestResponse>>(
				`${this.urlBase}insertarSolicitud`,
				digitalRequest,
				{ headers }
			)
			.pipe(
				map((response: CobisCmvHttpResponse<DigitalRequestResponse>) => {
					if (response.estado && response.codigo === 200) {
						return response.modelo;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	getDataForDigitalRequestAndMakeRequest(): Observable<DigitalRequestCreationResult> {
		return new Observable<DigitalRequestCreationResult>(observer => {
			this.store
				.select(selectPreAplicationState)
				.pipe(take(1))
				.subscribe({
					next: preApplicationState => {
						this.createRequestAndInsertDigitalRequest(
							preApplicationState,
							observer
						);
					},
				});
		});
	}

	private createRequestAndInsertDigitalRequest(
		preApplicationState: PreApplicationState,
		observer: Observer<DigitalRequestCreationResult>
	) {
		const request: DigitalRequest = createDigitalRequestBody(
			preApplicationState.productId,
			preApplicationState
		);

		this.insertDigitalRequest(request).subscribe({
			next: response => {
				const applicationId = response.solicitud_digital_id;
				this.store.dispatch(
					PreApplicationActions.addApplicationId({ applicationId })
				);
				observer.next({ status: 'created' });
				observer.complete();
			},
			error: (error: HttpErrorResponse | Error) => {
				const detail =
					error instanceof HttpErrorResponse
						? this.parseInsertDigitalRequestError(error)
						: error?.message;
				const alreadyExists =
					typeof detail === 'string' &&
					detail.toLowerCase().includes('already exists');

				if (alreadyExists) {
					observer.next({
						status: 'alreadyExists',
						folioDigital: preApplicationState.folio,
						errorDetail: detail,
					});
					observer.complete();
					return;
				}

				observer.error(
					new Error(
						detail ?? 'Ocurrio un error al guardar la solicitud digital.'
					)
				);
				observer.complete();
			},
		});
	}

	private parseInsertDigitalRequestError(error: HttpErrorResponse): string {
		return resolveErrorMessage(
			error,
			'Ocurrio un error al guardar la solicitud digital.'
		);
	}

	savePersonalJob(personalJobRequest: PersonalJobRequest): Observable<boolean> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this._http
			.post<CmvHttpEntidadResponse<string>>(
				`${this.urlBase}personalJob`,
				personalJobRequest,
				{ headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<string>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Estado;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getDigitalProcess(
		gigitalProcessRequest: DigitalProcessRequest
	): Observable<DigitalProcessResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this._http
			.post<CmvHttpEntidadResponse<DigitalProcessResponse>>(
				`${this.urlBase}getProcessDigital`,
				gigitalProcessRequest,
				{ headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<DigitalProcessResponse>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	updateFromSendTdd(
		locationToSendCardRequest: LocationToSendCardRequest
	): Observable<boolean> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this._http
			.post<CmvHttpEntidadResponse<null>>(
				`${this.urlBase}updateFromSendTdd`,
				locationToSendCardRequest,
				{ headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<null>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Estado;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getProcessProduct(subProductId: number): Observable<Object> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();

		const params = new HttpParams().set('id_producto', subProductId);

		return this._http
			.post<CmvHttpEntidadResponse<Object>>(
				`${this.urlBase}getProcessProduct`,
				null,
				{ headers, params }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Object>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getDocuments(
		memberId: number,
		applicationId: string
	): Observable<Map<string, Array<string>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('numero_socio', memberId)
			.set('id_sol_credito', applicationId);

		return this._http
			.post<CmvHttpEntidadResponse<Map<string, Array<String>>>>(
				`${this.urlBase}getDocs`,
				null,
				{
					params,
					headers,
				}
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Map<string, Array<string>>>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getDocumentPdf(
		memberId: number,
		creditApplicationId: string,
		documentName: string
	): Observable<string> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('numero_socio', memberId)
			.set('id_sol_credito', creditApplicationId)
			.set('nombre_archivo', documentName);

		return this._http
			.post<CmvHttpEntidadResponse<string>>(
				`${this.urlBase}downloadDoc`,
				null,
				{
					params,
					headers,
				}
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<string>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getProductBy(productId: number): Observable<ProductResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams().set('id_producto', productId);
		return this._http
			.post<CmvHttpEntidadResponse<Array<ProductResponse>>>(
				`${this.urlBase}get_products`,
				null,
				{ headers, params }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Array<ProductResponse>>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad[0];
					}

					throw new Error(response.Mensaje);
				})
			);
	}

	updateProofOfAddress(
		proofOfAddressRequest: ProofOfAddressRequest
	): Observable<{ valido: boolean; motivo_invalido: string }> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<CobisCmvHttpResponse<any>>(
				`${this.urlBase}updateCompAdress`,
				proofOfAddressRequest,
				{
					headers: headers,
				}
			)
			.pipe(
				map((response: CobisCmvHttpResponse<any>) => {
					const { estado, codigo } = response;
					if (estado && codigo === 200) {
						return response.modelo;
					}
					if (codigo === '400') {
						let error: Error = new Error(response.mensaje);
						error.name = 'Validation';
						throw error;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	getAvailableCertificates(
		productId: number,
		postalCode: number
	): Observable<CertificatesResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('id_producto', productId)
			.set('codigo_postal', postalCode);
		return this._http
			.post<CmvHttpEntidadResponse<CertificatesResponse>>(
				`${this.urlBase}getAvailableCertificates`,
				null,
				{
					headers,
					params,
				}
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<CertificatesResponse>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getOfferByProduct(
		memberNumber: number,
		productCode: string
	): Observable<OfferResponse> {
		const normalizedProductCode = productCode?.toUpperCase();
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams()
			.set('id_socio', memberNumber)
			.set('codigo_producto', normalizedProductCode);
		return this._http
			.get<OfferResponse | CobisCmvHttpResponse<OfferResponse> | MotorOfferErrorResponse>(
				`${this.urlBase}ofertasMotor`,
				{
					headers,
					params,
				}
			)
			.pipe(
				map((response: OfferResponse | CobisCmvHttpResponse<OfferResponse> | MotorOfferErrorResponse) => {
					if (this.isCobisResponse(response)) {
						if (response.estado && response.codigo == '200') {
							return response.modelo;
						}
						throw new Error(response.mensaje);
					}
					if (this.isMotorOfferError(response)) {
						throw new Error(
							`No se pudo obtener una oferta de crédito ${normalizedProductCode} para el socio ${memberNumber}.`
						);
					}
					return response as OfferResponse;
				}),
				catchError((error: HttpErrorResponse | Error) => {
					if (error instanceof Error) {
						return throwError(() => error);
					}
					if (error.status === 404) {
						return throwError(
							() =>
								new Error(
									`No se pudo obtener una oferta de crédito ${normalizedProductCode} para el socio ${memberNumber}.`
								)
						);
					}
					return throwError(() => error);
				})
			);
	}

	generateEvaluation(
		memberNumber: number,
		productCode: string
	): Observable<boolean> {
		const normalizedProductCode = productCode?.toUpperCase();
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const body: EvaluationDataRequest = {
			numero_socio: memberNumber,
			codigo_producto: normalizedProductCode,
		};
		return this._http
			.post<CobisCmvHttpResponse<boolean>>(
				`${this.urlBase}generarEvaluacion`,
				body,
				{
					headers,
				}
			)
			.pipe(
				map((response: CobisCmvHttpResponse<boolean>) => {
					const { estado, codigo, mensaje, modelo } = response;
					if (
						estado &&
						(codigo === 200 ||
							codigo === '200' ||
							codigo === 0 ||
							codigo === '0')
					) {
						return !!modelo;
					}
					if (codigo === 500 || codigo === '500') {
						throw new Error(ERROR_MESSAGES.OFFER_EVALUATION_FAILED);
					}
					throw new Error(mensaje || ERROR_MESSAGES.OFFER_EVALUATION_FAILED);
				}),
				catchError((error: HttpErrorResponse | Error) => {
					if (error instanceof Error) {
						return throwError(() => error);
					}
					if (error.status === 500) {
						return throwError(
							() => new Error(ERROR_MESSAGES.OFFER_EVALUATION_FAILED)
						);
					}
					return throwError(() => error);
				})
			);
	}

	validatePreApplicationIdentity(
		preApplicationId: number
	): Observable<PreApplicationValidationResult> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const body = { id_presolicitud: preApplicationId };
		return this._http
			.put<CobisCmvHttpResponse<PreApplicationValidationModel>>(
				`${this.urlBase}validaciones_identidad`,
				body,
				{ headers }
			)
			.pipe(
				map(response =>
					this.parsePreApplicationValidationResponse(
						response,
						'No se pudo validar la identidad.'
					)
				)
			);
	}

	checkExistingOffer(
		preApplicationId: number
	): Observable<PreApplicationValidationResult> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const body = { id_presolicitud: preApplicationId };
		return this._http
			.put<CobisCmvHttpResponse<PreApplicationValidationModel>>(
				`${this.urlBase}oferta_existente`,
				body,
				{ headers }
			)
			.pipe(
				map(response =>
					this.parsePreApplicationValidationResponse(
						response,
						'No se pudo validar la oferta existente.'
					)
				)
			);
	}

	getInitialInformation(
		productCode: string,
		memberNumber: number
	): Observable<InitialInformationModel> {
		const normalizedProductCode = productCode?.toUpperCase();
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams()
			.set('codigo_producto_core', normalizedProductCode)
			.set('id_persona', memberNumber);
		return this._http
			.get<CobisCmvHttpResponse<InitialInformationModel>>(
				`${this.urlBase}initialInformation`,
				{
					headers,
					params,
				}
			)
			.pipe(
				map((response: CobisCmvHttpResponse<InitialInformationModel>) => {
					if (response.estado && response.codigo == '200') {
						return response.modelo;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	getOfferRoutes(
		productCode: string,
		memberNumber?: number
	): Observable<FullProcessResponse[]> {
		const normalizedProductCode = productCode?.toUpperCase();
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		let params = new HttpParams().set('codigo_producto', normalizedProductCode);
		if (typeof memberNumber === 'number' && !Number.isNaN(memberNumber)) {
			params = params.set('id_persona', memberNumber);
		}
		return this._http
			.get<CobisCmvHttpResponse<FullProcessResponse[]> | FullProcessResponse[]>(
				`${this.urlBase}rutas_proceso`,
				{
					headers,
					params,
				}
			)
			.pipe(
				map(response => {
					if (this.isCobisResponse(response)) {
						if (
							response.estado &&
							(response.codigo === 200 || response.codigo === '200')
						) {
							return response.modelo;
						}
						throw new Error(
							response.mensaje ||
								'No se pudieron obtener las rutas del proceso.'
						);
					}

					if (Array.isArray(response)) {
						return response;
					}
					throw new Error('Respuesta invalida en rutas_proceso.');
				})
			);
	}

	getContactData(id_persona_contacto: number): Observable<ContactData> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams().set(
			'id_persona_contacto',
			id_persona_contacto
		);
		return this._http
			.get<CobisCmvHttpResponse<ContactData>>(
				`${this.urlBase}obtener_datos_contacto`,
				{
					headers,
					params,
				}
			)
			.pipe(
				map((response: CobisCmvHttpResponse<ContactData>) => {
					if (response.estado && response.codigo == '200') {
						return response.modelo;
					}
					throw new Error(response.mensaje);
				})
			);
	}

	private isCobisResponse<T>(
		response: T | CobisCmvHttpResponse<T>
	): response is CobisCmvHttpResponse<T> {
		return (
			response !== null &&
			typeof response === 'object' &&
			'estado' in response &&
			'codigo' in response
		);
	}

	private isMotorOfferError(
		response: object
	): response is MotorOfferErrorResponse {
		return 'code' in response && 'message' in response && !('monto' in response);
	}

	private parsePreApplicationValidationResponse(
		response: CobisCmvHttpResponse<PreApplicationValidationModel>,
		fallbackMessage: string
	): PreApplicationValidationResult {
		const { estado, codigo, mensaje, modelo } = response;
		if (
			estado &&
			(codigo === 200 || codigo === '200' || codigo === 0 || codigo === '0')
		) {
			const reason =
				modelo?.motivo_rechazo_identidad_validator ??
				modelo?.motivo_rechazo_oferta_existente ??
				modelo?.motivo_rechazo ??
				'';
			return {
				isRejected: !!modelo?.is_presolicitud_rechazada,
				reason,
			};
		}
		throw new Error(mensaje || fallbackMessage);
	}
	private getFormsVisualizationToken(): Observable<string> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		return this._http
			.post<
				CobisCmvHttpResponse<{
					access_token: string;
					token_type: string;
					expires_in?: number;
					scope?: string;
				}>
			>(`${this.urlBase}forms_visualization_auths`, null, { headers })
			.pipe(
				map(res => {
					const accessToken = res?.modelo?.access_token;
					if (accessToken) {
						return accessToken;
					}
					throw new Error(
						res?.mensaje || 'No se pudo obtener el token de visualización.'
					);
				})
			);
	}

	postObtenerPlantillaDocumento(documento: string): Observable<Blob> {
		return this.getFormsVisualizationToken().pipe(
			switchMap(accessToken => {
				let headers = new HttpHeadersBuilder()
					.withClientId()
					.withChannelIdGd()
					.getHeaders()
					.set('Authorization', `Bearer ${accessToken}`);
				return this._http.post(
					`${this.urlBase}obtenerPlantillaDocumento?id_formato=${documento}`,
					null,
					{
						headers,
						responseType: 'blob' as const,
					}
				);
			})
		);
	}

	getListExternal(id_socio: number, token: string): Observable<any> {
		let headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		const requestId = crypto.randomUUID();
		const correlacionId = crypto.randomUUID();
		headers = headers
			.set('X-IBM-Client-Id', environment.clientID)
			.set('Authorization', `${token}`)
			.set('X-Branch-ID', environment.x_branch_Id)
			.set('X-Channel-ID', environment.x_channel_Id)
			.set('X-Api-Version', environment.x_api_version)
			.set('X-Request-ID', requestId.toString())
			.set('X-Correlacion-ID', correlacionId.toString());
		const url = `${this.urlBase}api/socios/${id_socio}/coincidencias_listas`;
		return this._http
			.get<ExternListCmvHttpResponse<any>>(url, {
				headers,
			})
			.pipe(
				map((response: ExternListCmvHttpResponse<any>) => {
					if (response.coincidencias != null) {
						return response.coincidencias;
					}
					throw new Error('lista interna vacía');
				})
			);
	}

	getListIntern(
		numero: number,
		Rol_Id: string,
		token: string
	): Observable<any> {
		let headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		console.log('INTERNTOKEN:', token);
		headers = headers
			.set('X-IBM-Client-Id', environment.clientID)
			.set('Authorization', `${token}`);

		const params = new HttpParams().set('numero', numero).set('Rol_Id', Rol_Id);

		return this._http
			.get<InternListCmvHttpResponse<any>>(
				`${this.urlBase}obtenerListaInterna`,
				{
					headers,
					params,
				}
			)
			.pipe(
				map((response: InternListCmvHttpResponse<any>) => {
					if (response.coincidencias != null) {
						return response.coincidencias;
					}
					throw new Error('lista interna vacia');
				})
			);
	}
	getTokenPLD(): Observable<CobisCmvTokenHttpResponse<any>> {
		const body = new HttpParams()
			.set('client_id', environment.client_id)
			.set('grant_type', environment.grant_type)
			.set('client_secret', environment.client_secret);

		let headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();

		headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

		return this._http
			.post<CobisCmvTokenHttpResponse<any>>(
				`${this.urlBase}obtenerToken`,
				body.toString(),
				{ headers }
			)
			.pipe(
				map(response => {
					if (response?.Authorization && response?.Type) {
						return response;
					}
					throw new Error('Error al generar el token');
				})
			);
	}
}
